import React from "react";
import type {
  AssuranceAssessment,
  AssuranceFinding,
  AssuranceLink,
  AssuranceRepository,
  AssuranceViewDefinition,
} from "../../assurance/types";
import { EmptyPanel } from "./primitives";
import {
  ApplicationView,
  CapabilityView,
  PortfolioView,
} from "./views/ExecutiveViews";
import { ArchitectureView, CoverageView } from "./views/EvidenceViews";
import {
  CampaignView,
  FindingEvidenceView,
  FindingsInventoryView,
  RemediationView,
} from "./views/FindingsViews";
import {
  CloudView,
  HealthView,
  LicensesView,
  QualityView,
  SupplyChainView,
} from "./views/EngineeringViews";
import {
  BenchmarkView,
  ComplianceView,
  PrGuardView,
} from "./views/GovernanceViews";
import {
  DeploymentView,
  ReplacementView,
  ReportsView,
} from "./views/TrustViews";
import { ReportWorkspaceView } from "./views/ReportWorkspace";
import { selectScanState } from "../../assurance/model";

export interface AssuranceViewContentProps {
  assessment: AssuranceAssessment;
  view: AssuranceViewDefinition;
  selectedFindingId?: string;
  /** One-shot severity filter applied when arriving at the findings view. */
  presetSeverity?: string;
  onSelectFinding: (finding: AssuranceFinding, openEvidence?: boolean) => void;
  onSelectView: (view: string, params?: Record<string, string>) => void;
  endpointLinks: AssuranceLink[];
  /** Live-mode portfolio repositories (empty in demo mode). */
  repositories?: AssuranceRepository[];
  selectedRepoId?: string;
  onSelectRepo?: (repoId: string) => void;
}

/**
 * Routes the active view id to its dedicated component. Every one of the 20
 * public view ids has a purpose-built screen; ids are a stable deep-link
 * contract, so unknown ids fall back to an honest empty state rather than a
 * generic data dump.
 */
export default function AssuranceViewContent({
  assessment,
  view,
  selectedFindingId,
  presetSeverity,
  onSelectFinding,
  onSelectView,
  endpointLinks,
  repositories = [],
  selectedRepoId,
  onSelectRepo,
}: AssuranceViewContentProps) {
  const demo = assessment.mode === "demo";

  const onSelectFindingId = (findingId: string) => {
    const finding = assessment.findings.find((item) => item.id === findingId);
    if (finding) onSelectFinding(finding, true);
  };

  switch (view.id) {
    case "application":
      return (
        <ApplicationView
          assessment={assessment}
          demo={demo}
          onSelectView={onSelectView}
          onSelectFinding={onSelectFinding}
        />
      );
    case "portfolio":
      return (
        <PortfolioView
          assessment={assessment}
          demo={demo}
          repositories={repositories}
          selectedRepoId={selectedRepoId}
          onSelectRepo={onSelectRepo}
          onSelectView={onSelectView}
        />
      );
    case "capability":
      return <CapabilityView assessment={assessment} demo={demo} />;
    case "coverage":
      return <CoverageView assessment={assessment} demo={demo} />;
    case "architecture":
      return (
        <ArchitectureView
          assessment={assessment}
          demo={demo}
          onSelectFindingId={onSelectFindingId}
        />
      );
    case "findings":
      return (
        <FindingsInventoryView
          assessment={assessment}
          demo={demo}
          selectedFindingId={selectedFindingId}
          presetSeverity={presetSeverity}
          onSelectFinding={onSelectFinding}
        />
      );
    case "evidence":
      return (
        <FindingEvidenceView
          assessment={assessment}
          demo={demo}
          selectedFindingId={selectedFindingId}
          onSelectFinding={onSelectFinding}
          onSelectView={onSelectView}
        />
      );
    case "remediation":
      return (
        <RemediationView
          assessment={assessment}
          demo={demo}
          onSelectFindingId={onSelectFindingId}
        />
      );
    case "campaign":
      return <CampaignView assessment={assessment} demo={demo} />;
    case "supply-chain":
      return (
        <SupplyChainView
          assessment={assessment}
          demo={demo}
          sbomLinks={endpointLinks.filter(
            (link) => (link.kind || "").toLowerCase() === "sbom"
          )}
        />
      );
    case "licenses":
      return <LicensesView assessment={assessment} demo={demo} />;
    case "cloud":
      return (
        <CloudView
          assessment={assessment}
          demo={demo}
          onSelectFindingId={onSelectFindingId}
        />
      );
    case "quality":
      return <QualityView assessment={assessment} demo={demo} />;
    case "health":
      return <HealthView assessment={assessment} demo={demo} />;
    case "compliance":
      return (
        <ComplianceView
          assessment={assessment}
          demo={demo}
          onSelectFindingId={onSelectFindingId}
        />
      );
    case "benchmark":
      return <BenchmarkView assessment={assessment} demo={demo} />;
    case "pr-guard":
      return <PrGuardView assessment={assessment} demo={demo} />;
    case "reports":
      return demo ? (
        <ReportsView
          assessment={assessment}
          demo={demo}
          endpointLinks={endpointLinks}
        />
      ) : (
        <ReportWorkspaceView
          assessment={assessment}
          repoId={selectedRepoId || assessment.repository?.id}
          scanState={selectScanState(assessment)}
        />
      );
    case "deployment":
      return (
        <DeploymentView
          assessment={assessment}
          demo={demo}
          onSelectFindingId={onSelectFindingId}
        />
      );
    case "replacement":
      return <ReplacementView assessment={assessment} demo={demo} />;
    default:
      return (
        <EmptyPanel
          demo={demo}
          title="Unknown view"
          detail={`No view is registered for id "${view.id}".`}
        />
      );
  }
}
